import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { startVoice } from "@/lib/realtimeVoice";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mic, MicOff, MessageSquareHeart, Loader2, Check, Sparkles, AlertTriangle, ThumbsUp, Lightbulb } from "lucide-react";

const sid = () => `fb${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// "Tell OCTON what you think": a live voice interview about the app. Every turn is saved as it happens,
// then OCTON writes a brief for the team and the toddler (OCTON DNA).
export function FeedbackDialog({ open, onOpenChange }) {
  const [status, setStatus] = useState("idle");
  const [lines, setLines] = useState([]);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const session = useRef(sid());
  const voice = useRef(null);
  const end = useRef(null);
  useEffect(() => { if (open) { session.current = sid(); setLines([]); setTyped(""); setBrief(null); } }, [open]);
  useEffect(() => { const el = end.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [lines.length]);
  useEffect(() => () => { voice.current?.stop(); }, []);
  const save = (role, text) => api.post("/api/feedback/turn", { session_id: session.current, role, text }).catch(() => {});
  const start = async () => {
    try {
      voice.current = await startVoice({
        topic: "feedback", onStatus: setStatus,
        onEvent: (ev) => {
          const t = ev.type || "";
          if (t === "conversation.item.input_audio_transcription.completed" && ev.transcript?.trim()) {
            const text = ev.transcript.trim(); setLines((l) => [...l, { role: "user", text }]); save("user", text);
          }
          if ((t === "response.audio_transcript.done" || t === "response.output_audio_transcript.done") && ev.transcript?.trim()) {
            const text = ev.transcript.trim(); setLines((l) => [...l, { role: "assistant", text }]); save("assistant", text);
          }
        },
      });
    } catch (err) { toast.error(apiErr(err, "Couldn't start voice. Allow the microphone and try again.")); setStatus("idle"); voice.current = null; }
  };
  const stop = () => { voice.current?.stop(); voice.current = null; setStatus("idle"); };
  const finish = async () => {
    stop(); setBusy(true);
    try {
      const { data } = await api.post("/api/feedback/finish", { session_id: session.current, typed });
      setBrief(data.brief || {}); toast.success("Thank you. Your feedback is with the OCTON team.");
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  const close = (o) => { if (!o) stop(); onOpenChange(o); };
  const live = status === "live";
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="surface border-[#1E222A] max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Tell OCTON what you think</DialogTitle></DialogHeader>
        {brief ? (
          <div className="space-y-3" data-testid="feedback-brief">
            <p className="text-sm text-[#D7DCE5]">{brief.summary || "Thanks, we have it."}</p>
            {brief.top_actions?.length > 0 && (
              <div className="rounded-xl border border-acid/40 bg-acid/5 p-3">
                <p className="mono-label text-acid mb-1">what we'll look at first</p>
                {brief.top_actions.map((a, i) => <p key={i} className="text-sm flex gap-2"><Check className="w-4 h-4 text-acid shrink-0 mt-0.5" /> {a}</p>)}
              </div>
            )}
            <button onClick={() => close(false)} className="w-full h-11 rounded-full bg-acid text-black font-medium">Done</button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="feedback-voice">
            <p className="text-sm text-[#8A91A6]">Tap the microphone and talk to OCTON, or just type below. Tell us what you like, what confused you and what's missing. Everything goes straight to the team.</p>
            <div className="flex flex-col items-center gap-3 py-2">
              <motion.button onClick={live ? stop : start} whileTap={{ scale: 0.95 }} data-testid="feedback-mic"
                className={`relative w-28 h-28 rounded-full flex items-center justify-center ${live ? "bg-acid text-black" : "bg-[#171A21] border border-acid/50 text-acid"}`}>
                {live && <motion.span className="absolute inset-0 rounded-full border-2 border-acid" animate={{ scale: [1, 1.35], opacity: [0.7, 0] }} transition={{ duration: 1.4, repeat: Infinity }} />}
                {status === "connecting" ? <Loader2 className="w-10 h-10 animate-spin" /> : live ? <MicOff className="w-10 h-10" /> : <Mic className="w-10 h-10" />}
              </motion.button>
              <p className="mono-label">{status === "connecting" ? "connecting…" : live ? "listening · tap to pause" : "tap to talk to OCTON"}</p>
            </div>
            {lines.length > 0 && (
              <div className="max-h-56 overflow-y-auto space-y-2 rounded-xl border border-[#1E222A] p-3">
                {lines.map((l, i) => (
                  <p key={i} className={`text-sm ${l.role === "user" ? "text-white" : "text-acid"}`}><span className="mono-label mr-1">{l.role === "user" ? "you" : "octon"}</span>{l.text}</p>
                ))}
                <div ref={end} />
              </div>
            )}
            <textarea className="w-full rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 py-2 text-sm" rows={2}
              placeholder="Prefer to type? Add anything here too." value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="feedback-typed" />
            <button disabled={busy || (!lines.some((l) => l.role === "user") && !typed.trim())} onClick={finish} data-testid="feedback-send"
              className="w-full h-11 rounded-full bg-acid text-black font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> OCTON is writing it up…</> : "End and send to the team"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackCard() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={() => setOpen(true)} data-testid="feedback-card"
        className="w-full relative overflow-hidden rounded-2xl border border-[#B57BFF]/40 bg-gradient-to-r from-[#1A1226] to-[#0B0D11] p-5 text-left group">
        <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full blur-3xl opacity-25 group-hover:opacity-40" style={{ background: "#B57BFF" }} />
        <div className="relative flex items-center gap-4">
          <span className="w-12 h-12 rounded-full bg-[#B57BFF]/20 border border-[#B57BFF]/50 flex items-center justify-center shrink-0"><MessageSquareHeart className="w-6 h-6 text-[#B57BFF]" /></span>
          <div className="flex-1">
            <p className="font-heading font-bold">Help shape OCTON Gym</p>
            <p className="text-sm text-[#B7BECB]">Tell OCTON by voice what you love, what's confusing and what's missing. Two minutes, and it goes straight to the team.</p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-2 rounded-full h-10 px-4 bg-[#B57BFF] text-black font-medium shrink-0"><Mic className="w-4 h-4" /> Talk to OCTON</span>
        </div>
      </motion.button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

// Always-visible "Feedback" button (phone header and sidebar): opens the same voice or text dialog
export function FeedbackButton({ className = "", label = "Feedback", testid = "feedback-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testid} className={className}>
        <MessageSquareHeart className="w-4 h-4" /> {label}
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

// Admin: every feedback brief, newest first
export function FeedbackInbox() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-feedback"], queryFn: async () => (await api.get("/api/admin/feedback")).data, refetchInterval: 30000 });
  const [show, setShow] = useState(null);
  const setStatus = async (f, status) => { await api.post(`/api/admin/feedback/${f.id}/status`, { status }); qc.invalidateQueries({ queryKey: ["admin-feedback"] }); };
  const SEV = { high: "#FF5A5F", medium: "#FFB340", low: "#8A91A6" };
  return (
    <div className="surface rounded-2xl p-5" data-testid="feedback-inbox">
      <p className="font-heading font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B57BFF]" /> Member feedback</p>
      <p className="text-sm text-[#8A91A6] mt-1">Voice interviews and typed notes, written up by OCTON. Shared with OCTON DNA so it can learn from them.</p>
      {data.length === 0 && <p className="text-sm text-[#565C6F] mt-3">Nothing yet. Members can tap "Talk to OCTON" on their dashboard.</p>}
      <div className="mt-3 space-y-3">
        {data.map((f) => {
          const b = f.brief || {};
          return (
            <div key={f.id} className="rounded-xl border border-[#1E222A] bg-[#0B0D11] p-4">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <p className="text-sm"><span className="font-semibold">{f.name}</span> <span className="text-[#8A91A6]">· {f.role}{f.club_name ? ` · ${f.club_name}` : ""} · {new Date(f.created_at).toLocaleString()}</span></p>
                <select value={f.status} onChange={(e) => setStatus(f, e.target.value)} className="h-8 rounded-lg bg-[#0F1115] border border-[#1E222A] px-2 text-xs">
                  <option value="new">New</option><option value="planned">Planned</option><option value="done">Done</option><option value="wont_do">Won't do</option>
                </select>
              </div>
              {b.summary && <p className="text-sm text-[#D7DCE5] mt-2">{b.summary}</p>}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                <div><p className="mono-label flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> problems</p>{(b.problems || []).map((p, i) => <p key={i} className="mt-1"><span style={{ color: SEV[p.severity] }}>●</span> {p.what}{p.where ? <span className="text-[#565C6F]"> ({p.where})</span> : null}</p>)}</div>
                <div><p className="mono-label flex items-center gap-1"><Lightbulb className="w-3 h-3" /> wishes</p>{(b.requests || []).map((r, i) => <p key={i} className="mt-1">{r.what}</p>)}</div>
                <div><p className="mono-label flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> loved</p>{(b.praise || []).map((p, i) => <p key={i} className="mt-1">{p}</p>)}</div>
              </div>
              {b.top_actions?.length > 0 && <p className="text-xs text-acid mt-3">First actions: {b.top_actions.join(" · ")}</p>}
              <button onClick={() => setShow(show === f.id ? null : f.id)} className="text-xs text-[#8A91A6] hover:text-white mt-2">{show === f.id ? "Hide" : "Show"} full conversation</button>
              <AnimatePresence>{show === f.id && <motion.pre initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-xs text-[#B7BECB] whitespace-pre-wrap max-h-72 overflow-y-auto">{f.transcript}</motion.pre>}</AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
